import { useEstadoNaUrl } from "@/lib/estado-na-url";
import { useNavegacao } from "@/lib/navegacao";
import { Skeleton } from "@/components/ui/skeleton";
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, ChevronRight, ClipboardList, Info, Trash2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import {
  useAuditorias, useAtividadesAuditoria, useCreateAuditoria, usePatchAuditoria, useDeleteAuditoria,
  useSetoresAuditoria, useFuncionariosAuditoria, useSugestaoResponsaveisAuditoria,
  parseErroAuditoria, nota1, corDaNota,
  type TipoAuditoria, type AuditoriaExistente, type AuditoriaResumo,
} from "@/hooks/use-auditoria";

import { PageHeader } from "@/components/layout/page-header";
import { PageToolbar, PageSearch } from "@/components/layout/page-toolbar";
import { DataCard } from "@/components/data/data-card";
import { StatusBadge } from "@/components/data/status-badge";
import { EmptyState, NadaEncontrado } from "@/components/data/empty-state";
import { PageSkeleton } from "@/components/data/page-skeleton";
import { combina } from "@/lib/busca";
// ─── Helpers locais ───────────────────────────────────────────────────────────

/** Data de hoje em AAAA-MM-DD no fuso local (toISOString puro erraria o dia à noite). */
function hojeISO(): string {
  const d = new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
}

/** AAAA-MM-DD → dd/mm/aaaa. */
function dataBR(iso: string | null | undefined): string {
  if (!iso) return "—";
  const [a, m, d] = iso.slice(0, 10).split("-");
  return a && m && d ? `${d}/${m}/${a}` : iso;
}

// ─── Dialog de criação ────────────────────────────────────────────────────────

function NovaAuditoriaDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const { toast } = useToast();
  const [, navigate] = useLocation();
  // Abre a auditoria recém-criada gravando a lista como origem.
  const { abrir: abrirNova } = useNavegacao();
  const { data: setores = [] } = useSetoresAuditoria();
  const { data: atividadesData, isLoading: carregandoAtividades } = useAtividadesAuditoria();
  const { data: todosFuncionarios = [] } = useFuncionariosAuditoria();
  const create = useCreateAuditoria();
  const patch = usePatchAuditoria();

  const [setorId, setSetorId] = useState("");
  const [data, setData] = useState(hojeISO());
  const [auditorNome, setAuditorNome] = useState("");
  const [tipo, setTipo] = useState<TipoAuditoria>("orientativa");
  const [selecionadas, setSelecionadas] = useState<string[]>([]);
  const [responsavelIds, setResponsavelIds] = useState<string[]>([]);
  const [respEditado, setRespEditado] = useState(false);
  const [editandoResp, setEditandoResp] = useState(false);
  const [conflito, setConflito] = useState<AuditoriaExistente | null>(null);
  const [resolvendoConflito, setResolvendoConflito] = useState(false);

  const { data: sugestao, isFetching: carregandoSugestao } = useSugestaoResponsaveisAuditoria(setorId || undefined);

  // Só aparecem atividades auditáveis e com ao menos um critério — nada de
  // ligar o interruptor em "Atividades e critérios" e não ver critério nenhum.
  const atividades = useMemo(
    () => (atividadesData ?? []).filter(a => a.criterios.length > 0),
    [atividadesData],
  );

  // Ao abrir, começa com todas as atividades marcadas — menos toques no celular.
  useEffect(() => {
    if (!open) return;
    setData(hojeISO());
    setSelecionadas(atividades.map(a => a.id));
    setSetorId("");
    setAuditorNome("");
    setTipo("orientativa");
    setResponsavelIds([]);
    setRespEditado(false);
    setEditandoResp(false);
    setConflito(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Muda de setor → esquece o ajuste pontual anterior; a sugestão do novo setor assume.
  useEffect(() => {
    setRespEditado(false);
    setEditandoResp(false);
  }, [setorId]);

  useEffect(() => {
    if (!respEditado) setResponsavelIds((sugestao ?? []).map(f => f.id));
  }, [sugestao, respEditado]);

  const toggle = (id: string) =>
    setSelecionadas(sel => (sel.includes(id) ? sel.filter(x => x !== id) : [...sel, id]));

  const toggleResp = (id: string) => {
    setResponsavelIds(ids => (ids.includes(id) ? ids.filter(x => x !== id) : [...ids, id]));
    setRespEditado(true);
  };

  const nomeFuncionario = (id: string) => todosFuncionarios.find(f => f.id === id)?.nome ?? "?";

  const podeSalvar = !!setorId && !!data && !!auditorNome.trim() && selecionadas.length > 0 && !create.isPending;

  const criar = async (tipoEnvio: TipoAuditoria) => {
    const nova = await create.mutateAsync({
      setorId,
      data,
      auditorNome: auditorNome.trim(),
      tipo: tipoEnvio,
      responsavelIds,
      atividadeIds: selecionadas,
    });
    toast({ title: "Auditoria criada!" });
    onOpenChange(false);
    abrirNova(`/manejo/auditoria/${nova.id}`);
  };

  const handleSalvar = async () => {
    if (!podeSalvar) return;
    try {
      await criar(tipo);
    } catch (e) {
      const erro = parseErroAuditoria(e, "Erro ao criar auditoria");
      if (erro.code === "FECHAMENTO_JA_EXISTE" && erro.auditoriaExistente) {
        setConflito(erro.auditoriaExistente);
        return;
      }
      toast({ title: "Erro ao criar auditoria", description: erro.message, variant: "destructive" });
    }
  };

  // Já existe fechamento do setor no mês: ou segue como orientativa, ou converte
  // a existente para orientativa (liberando o mês) e prossegue como fechamento.
  const handleCriarComoOrientativa = async () => {
    setResolvendoConflito(true);
    try {
      await criar("orientativa");
    } catch (e) {
      toast({ title: "Erro ao criar auditoria", description: parseErroAuditoria(e).message, variant: "destructive" });
    } finally {
      setResolvendoConflito(false);
    }
  };

  const handleConverterEProsseguir = async () => {
    if (!conflito) return;
    setResolvendoConflito(true);
    try {
      await patch.mutateAsync({ id: conflito.id, data: { tipo: "orientativa" } });
      setConflito(null);
      await criar("fechamento");
    } catch (e) {
      toast({ title: "Erro ao criar auditoria", description: parseErroAuditoria(e).message, variant: "destructive" });
    } finally {
      setResolvendoConflito(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Nova auditoria</DialogTitle></DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Setor</Label>
            <Select value={setorId || undefined} onValueChange={setSetorId}>
              <SelectTrigger className="h-11"><SelectValue placeholder="Selecione o setor..." /></SelectTrigger>
              <SelectContent>
                {setores.map(s => <SelectItem key={s.id} value={s.id}>{s.nome}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="row grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Data</Label>
              <Input type="date" className="h-11" value={data} onChange={e => setData(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Auditor</Label>
              <Input
                className="h-11"
                value={auditorNome}
                onChange={e => setAuditorNome(e.target.value)}
                placeholder="Quem está auditando"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Tipo</Label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setTipo("orientativa")}
                className={`rounded-md border p-3 text-left transition-colors ${
                  tipo === "orientativa" ? "border-primary bg-primary/5" : "border-input"
                }`}
              >
                <span className="text-sm font-medium">Orientativa</span>
                <p className="text-xs text-muted-foreground mt-0.5">direciona o time; não vai para o Financeiro</p>
              </button>
              <button
                type="button"
                onClick={() => setTipo("fechamento")}
                className={`rounded-md border p-3 text-left transition-colors ${
                  tipo === "fechamento" ? "border-primary bg-primary/5" : "border-input"
                }`}
              >
                <span className="text-sm font-medium">Fechamento</span>
                <p className="text-xs text-muted-foreground mt-0.5">é a nota do mês deste setor</p>
              </button>
            </div>
          </div>

          <Separator />

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="mb-0">Responsáveis pelo setor</Label>
              {setorId && (
                <Button
                  variant="ghost" size="sm" className="h-7 px-2 text-xs"
                  onClick={() => setEditandoResp(v => !v)}
                >
                  {editandoResp ? "pronto" : "editar"}
                </Button>
              )}
            </div>

            {!setorId ? (
              <p className="text-sm text-muted-foreground">Escolha o setor.</p>
            ) : editandoResp ? (
              <>
                <div className="flex flex-wrap gap-1.5">
                  {todosFuncionarios.map(f => (
                    <button
                      key={f.id}
                      type="button"
                      onClick={() => toggleResp(f.id)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                        responsavelIds.includes(f.id)
                          ? "bg-primary text-primary-foreground border-primary"
                          : "bg-background text-muted-foreground border-input"
                      }`}
                    >
                      {f.nome}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">
                  Alteração pontual — vale só para esta auditoria. O cadastro de funcionários não muda.
                </p>
              </>
            ) : carregandoSugestao ? (
              <Skeleton className="h-6 w-40" />
            ) : (
              <>
                <div className="flex flex-wrap gap-1.5">
                  {responsavelIds.length ? (
                    responsavelIds.map(id => (
                      <span key={id} className="px-3 py-1 rounded-full text-xs font-medium bg-muted">
                        {nomeFuncionario(id)}
                      </span>
                    ))
                  ) : (
                    <span className="text-sm text-muted-foreground">
                      Nenhum encarregado ativo vinculado a este setor.
                    </span>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  {respEditado
                    ? "Ajustado só para esta auditoria."
                    : responsavelIds.length
                      ? "Vem do cadastro de funcionários — encarregado(s) ativo(s) deste setor."
                      : 'Vincule o encarregado no cadastro de funcionários, ou use "editar".'}
                </p>
              </>
            )}
          </div>

          <Separator />

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="mb-0">Atividades a auditar</Label>
              <span className="text-xs text-muted-foreground">{selecionadas.length} de {atividades.length}</span>
            </div>

            {carregandoAtividades ? (
              <Skeleton className="h-40 w-full" />
            ) : atividades.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">
                Nenhuma atividade auditável com critério cadastrado. Configure em "Atividades e critérios".
              </p>
            ) : (
              <div className="space-y-1">
                {atividades.map(a => (
                  <label
                    key={a.id}
                    className="flex items-center gap-3 rounded-md border p-3 cursor-pointer active:bg-accent"
                  >
                    <input
                      type="checkbox"
                      className="h-5 w-5 shrink-0 accent-primary"
                      checked={selecionadas.includes(a.id)}
                      onChange={() => toggle(a.id)}
                    />
                    <span className="flex-1 text-sm font-medium leading-tight">{a.nome}</span>
                  </label>
                ))}
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              Só aparecem as atividades marcadas como auditáveis em Atividades e critérios.
            </p>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" className="h-11" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button className="h-11" onClick={handleSalvar} disabled={!podeSalvar}>
            {create.isPending ? "Criando..." : "Lançar notas"}
          </Button>
        </DialogFooter>
      </DialogContent>

      <AlertDialog open={!!conflito} onOpenChange={v => !v && setConflito(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Já existe um fechamento para este setor neste mês</AlertDialogTitle>
            <AlertDialogDescription>
              {conflito && (
                <>
                  A auditoria de <strong>{dataBR(conflito.data)}</strong>
                  {conflito.status === "fechada" ? " (fechada" : " (rascunho"}, nota {nota1(conflito.notaFinal)})
                  já é o fechamento deste setor neste mês. Só pode existir uma. Como prefere seguir?
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-col gap-2">
            <AlertDialogAction
              onClick={e => { e.preventDefault(); handleCriarComoOrientativa(); }}
              disabled={resolvendoConflito}
              className="w-full"
            >
              Criar esta como orientativa
            </AlertDialogAction>
            <Button
              variant="outline" className="w-full"
              onClick={handleConverterEProsseguir}
              disabled={resolvendoConflito}
            >
              Converter a existente em orientativa e seguir com esta como fechamento
            </Button>
            <AlertDialogCancel disabled={resolvendoConflito} className="w-full mt-0">Cancelar</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}

// ─── Página ───────────────────────────────────────────────────────────────────

// ⚠️ Constante de MÓDULO (dep de useMemo dentro do hook).
const PADRAO_AUDITORIAS = { q: "" };

export default function AuditoriaIndex() {
  const { abrir } = useNavegacao();
  const { toast } = useToast();
  const { data: auditorias = [], isLoading } = useAuditorias();
  const [open, setOpen] = useState(false);
  // Busca NO CLIENTE, sobre a lista já carregada — mas na URL, para sobreviver
  // ao "Voltar" de uma auditoria aberta.
  const { estado, definir } = useEstadoNaUrl(PADRAO_AUDITORIAS);
  const busca = estado.q;
  const auditoriasVisiveis = auditorias.filter(a => combina(busca, a.setorNome, a.auditorNome));
  const [excluindo, setExcluindo] = useState<AuditoriaResumo | null>(null);
  const deleteAuditoria = useDeleteAuditoria();

  const handleExcluir = async () => {
    if (!excluindo) return;
    try {
      await deleteAuditoria.mutateAsync(excluindo.id);
      toast({ title: "Auditoria excluída" });
      setExcluindo(null);
    } catch (e) {
      toast({ title: "Erro ao excluir auditoria", description: parseErroAuditoria(e).message, variant: "destructive" });
    }
  };

  return (
    <div className="p-4 sm:p-6 space-y-4 pb-24">
      <PageHeader titulo="Auditorias" descricao="Nota por critério · média simples" />

      <Card className="bg-muted/40 border-dashed">
        <CardContent className="p-3 flex gap-2.5">
          <Info className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
          <p className="text-xs text-muted-foreground leading-relaxed">
            O auditor caminha o setor e dá <span className="font-medium text-foreground">uma nota de 0 a 10 para cada
            critério</span>. A nota do setor é a média simples de todas elas.
          </p>
        </CardContent>
      </Card>

      <Button className="w-full h-12 text-base" onClick={() => setOpen(true)}>
        <Plus className="h-5 w-5 mr-1.5" />Nova auditoria
      </Button>

      <PageToolbar
        fim={<PageSearch valor={busca} onChange={v => definir({ q: v })} placeholder="Setor" testId="input-busca-auditorias" />}
      >
        <span />
      </PageToolbar>

      {isLoading ? (
        <PageSkeleton variante="cards" linhas={3} />
      ) : auditorias.length === 0 ? (
        <EmptyState
          icone={ClipboardList}
          titulo="Nenhuma auditoria lançada ainda"
          descricao="Crie a primeira auditoria escolhendo o setor, a data, quem vai auditar e as atividades que serão avaliadas na caminhada."
          acao={<Button onClick={() => setOpen(true)}><Plus className="h-4 w-4 mr-1.5" />Nova auditoria</Button>}
          testId="empty-auditorias"
        />
      ) : auditoriasVisiveis.length === 0 ? (
        <NadaEncontrado termo={busca} />
      ) : (
        <div className="space-y-2">
          {auditoriasVisiveis.map(a => (
            <Card
              key={a.id}
              onClick={() => abrir(`/manejo/auditoria/${a.id}`)}
              className="cursor-pointer transition-colors active:bg-accent hover:bg-accent/50"
            >
              <CardContent className="p-4 flex items-center gap-3">
                <div className="min-w-0 flex-1 space-y-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold truncate">{a.setorNome ?? "Setor removido"}</span>
                    {/* Um badge por EIXO: `status` e `tipo` são independentes —
                        "fechada" + "fechamento" é estado válido e mostra os dois. */}
                    {a.status === "fechada"
                      ? <StatusBadge tom="sucesso" ponto>fechada</StatusBadge>
                      : <StatusBadge tom="info" ponto>rascunho</StatusBadge>}
                    {a.tipo === "fechamento" && <StatusBadge tom="neutro">fechamento</StatusBadge>}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {dataBR(a.data)} · {a.auditorNome}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">
                    {a.responsaveis.length ? a.responsaveis.map(r => r.nome).join(" · ") : "sem responsável"}
                    {" · "}{a.qtdCriteriosAvaliados} critério{a.qtdCriteriosAvaliados === 1 ? "" : "s"}
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <p className={`text-2xl font-bold leading-none ${corDaNota(a.notaFinal)}`}>{nota1(a.notaFinal)}</p>
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground mt-1">nota final</p>
                </div>
                <Button
                  variant="ghost" size="icon" className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
                  onClick={e => { e.stopPropagation(); setExcluindo(a); }}
                  aria-label="Excluir auditoria"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
                <ChevronRight className="h-5 w-5 text-muted-foreground shrink-0" />
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <NovaAuditoriaDialog open={open} onOpenChange={setOpen} />

      <AlertDialog open={!!excluindo} onOpenChange={v => !v && setExcluindo(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir auditoria</AlertDialogTitle>
            <AlertDialogDescription>
              {excluindo?.tipo === "fechamento" ? (
                <>
                  Esta é a auditoria de <strong>fechamento</strong> — a nota que conta para o mês de{" "}
                  <strong>{excluindo.setorNome ?? "setor removido"}</strong>. Excluir remove essa nota do resultado
                  do mês. Esta ação não pode ser desfeita.
                </>
              ) : (
                <>
                  Tem certeza que deseja excluir a auditoria de{" "}
                  <strong>{excluindo?.setorNome ?? "setor removido"}</strong> ({dataBR(excluindo?.data)})?
                  Esta ação não pode ser desfeita.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteAuditoria.isPending}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={e => { e.preventDefault(); handleExcluir(); }}
              disabled={deleteAuditoria.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteAuditoria.isPending ? "Excluindo..." : "Excluir"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
